"use client";

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronDown, LogOut, Settings, Shield } from "lucide-react";
import { Popover } from "@/lib/radix";
import { useTranslation } from "@/lib/hooks/use-translation";
import { ROUTES } from "@/lib/constants/routes";
import { APP_SHELL } from "@/lib/constants/workspace-layout";
import { cn } from "@/lib/utils";

export type HeaderAccountMenuProps = {
  displayName: string;
  membershipRoleLabel: string;
  organizationLabel: string;
  onSignOut: () => void;
};

function normalizeIdentityToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, "");
}

/** Humanize role ids: nice-org-ops → Nice Org Ops */
export function formatMembershipRoleLabel(raw: string): string {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) =>
      part
        .split(/[-_]+/)
        .filter(Boolean)
        .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
        .join(" "),
    )
    .join(" · ");
}

export function accountInitials(displayName: string): string {
  const cleaned = displayName.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

/** One primary name; role only when it adds information beyond the name. */
export function resolveAccountMenuIdentity(input: {
  displayName: string;
  membershipRoleLabel: string;
  organizationLabel: string;
}): { title: string; roleLabel: string; organizationLabel: string; initials: string } {
  const title = input.displayName.trim() || "Account";
  const formattedRole = formatMembershipRoleLabel(input.membershipRoleLabel);
  const roleDistinct =
    formattedRole.length > 0 && normalizeIdentityToken(formattedRole) !== normalizeIdentityToken(title);
  return {
    title,
    roleLabel: roleDistinct ? formattedRole : "",
    organizationLabel: input.organizationLabel.trim(),
    initials: accountInitials(title),
  };
}

export function AccountAvatar({
  initials,
  size = "md",
}: {
  initials: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizeClass =
    size === "sm" ? "h-7 w-7 text-[10px]" : size === "lg" ? "h-16 w-16 text-xl" : "h-11 w-11 text-sm";
  return (
    <span
      aria-hidden
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full font-semibold tracking-wide text-foreground",
        "bg-gradient-to-br from-[rgb(var(--nice-gold-rgb)/0.28)] via-muted to-muted",
        "ring-1 ring-[rgb(var(--nice-gold-rgb)/0.35)] shadow-sm",
        sizeClass,
      )}
    >
      <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_25%,rgb(var(--nice-gold-rgb)/0.35),transparent_55%)]" />
      <span className="relative">{initials}</span>
    </span>
  );
}

export function HeaderAccountMenu({
  displayName,
  membershipRoleLabel,
  organizationLabel,
  onSignOut,
}: HeaderAccountMenuProps): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const identity = useMemo(
    () =>
      resolveAccountMenuIdentity({
        displayName,
        membershipRoleLabel,
        organizationLabel,
      }),
    [displayName, membershipRoleLabel, organizationLabel],
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            APP_SHELL.userPill,
            "group gap-1.5 px-1.5 transition-colors duration-200 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring sm:pr-2",
            open && "bg-muted ring-2 ring-ring",
          )}
          aria-label={`${t("header.account")}: ${identity.title}`}
          title={identity.title}
          aria-expanded={open}
          aria-haspopup="menu"
          data-testid="header-account-menu-trigger"
        >
          <AccountAvatar initials={identity.initials} size="sm" />
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          className={cn(
            "z-[9999] w-[min(18.5rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-border",
            "surface-card p-0 shadow-2xl outline-none dark:shadow-black/50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          data-testid="header-account-menu"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="relative overflow-hidden border-b border-border px-4 pb-3.5 pt-4">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-[rgb(var(--nice-gold-rgb)/0.12)] blur-2xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[rgb(var(--nice-gold-rgb)/0.08)] via-transparent to-transparent"
            />
            <div className="relative flex items-start gap-3">
              <AccountAvatar initials={identity.initials} size="md" />
              <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                <p className="truncate text-sm font-semibold tracking-tight text-foreground" title={identity.title}>
                  {identity.title}
                </p>
                {identity.organizationLabel ? (
                  <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                    <span className="truncate" title={identity.organizationLabel}>
                      {identity.organizationLabel}
                    </span>
                  </p>
                ) : null}
                {identity.roleLabel ? (
                  <span
                    className={cn(
                      "inline-flex max-w-full items-center gap-1 rounded-full border border-border/80",
                      "bg-background/70 px-2 py-0.5 text-[11px] font-medium text-foreground/90",
                    )}
                    title={identity.roleLabel}
                  >
                    <Shield className="h-3 w-3 shrink-0 text-[rgb(var(--nice-gold-rgb)/0.9)]" aria-hidden />
                    <span className="truncate">{identity.roleLabel}</span>
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-1.5" role="menu" aria-label={t("header.account")}>
            <Link
              to={ROUTES.USER_ACCOUNT_SETTINGS}
              role="menuitem"
              data-testid="header-account-settings"
              className={cn(
                "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground",
                "outline-none transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
              )}
              onClick={() => setOpen(false)}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Settings className="h-4 w-4" />
              </span>
              <span className="font-medium">{t("header.settings")}</span>
            </Link>
            <button
              type="button"
              role="menuitem"
              data-testid="header-account-sign-out"
              className={cn(
                "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm",
                "outline-none transition-colors",
                "text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10 focus-visible:ring-2 focus-visible:ring-destructive/40",
              )}
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                <LogOut className="h-4 w-4" />
              </span>
              <span className="font-medium">{t("header.signOut")}</span>
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
